import React, { useState } from 'react';
import moment from 'moment-timezone';
import { message } from 'antd';
import { STAR_ACTION } from '../../data/workOrderStarLabels';
import { StarActionProps } from './connectStarActions';
import OWButton from '../ow_button/OWButton';
import { StarBadge, StarBadgeKind } from './StarBadge';

// The user's star as it was before a change — what UNDO puts back.
export interface StarSnapshot {
	note: string | null;
	remindAt: moment.Moment | null;
	// "Remind me only if…"; only meaningful with a reminder.
	condition?: string | null;
}

interface ToastAction {
	label: string;
	onClick: () => void;
}

// "WO #123" as a link that opens the work order in a new tab, so the toast doesn't navigate away.
// `roleType` is the session's (buyer | supplier), the same role the star write went to.
export const WorkOrderToastLink: React.FC<{
	workOrderId: number | string;
	roleType: string;
	onClick?: () => void;
}> = ({ workOrderId, roleType, onClick }) => (
	<a
		href={`/${roleType}/workOrders/detail/${workOrderId}/details`}
		target="_blank"
		rel="noopener noreferrer"
		onClick={onClick}
		className="inline-flex items-center gap-[5px]"
	>
		{`WO #${workOrderId}`}
		<i className="icons8-font icons8-external-link" />
	</a>
);

// `text` is the message before the work order link ("Tracked", "Set reminder for", ...);
// `showTime` adds when it happened ("at 3:46 PM") ahead of the buttons.
const showStarToast = (
	workOrderId: number | string,
	roleType: string,
	badge: StarBadgeKind,
	text: string,
	actions: ToastAction[] = [],
	showTime = false
) => {
	const toastKey = `star-${workOrderId}`;
	message.open({
		key: toastKey,
		content: (
			<span className="work-order-starred-feedback-toast">
				<StarBadge badge={badge} size="md" />
				<span className="work-order-starred-feedback-toast__text bodyregular text-character-primary">
					{`${text} `}
					<WorkOrderToastLink workOrderId={workOrderId} roleType={roleType} />
				</span>
				{showTime ? (
					<span className="bodyregular whitespace-nowrap text-character-secondary">
						{`at ${moment().format('h:mm A')}`}
					</span>
				) : null}
				{actions.map((action) => (
					<OWButton
						key={action.label}
						size="small"
						onClick={() => {
							message.destroy(toastKey);
							action.onClick();
						}}
					>
						{action.label}
					</OWButton>
				))}
			</span>
		),
		duration: actions.length > 0 ? 5 : 3,
	});
};

// The backend refuses a reminder time in the past, so a reminder that has already come due
// cannot be put back by UNDO; the star (and its note) is restored without it.
const restorableRemindAt = (remindAt: moment.Moment | null): moment.Moment | null =>
	remindAt && remindAt.isAfter(moment()) ? remindAt : null;

/**
 * Track / untrack / snooze / stop for one work order's star, each followed by the toast for
 * what the backend says happened (the PUT response's `action`). Every write goes to the API
 * first; the UI follows only on success.
 */
const useStarActions = (
	workOrderId: number | string,
	{ upsertWorkOrderStar, removeWorkOrderStar, roleType }: StarActionProps
) => {
	const toast = (
		badge: StarBadgeKind,
		text: string,
		actions: ToastAction[] = [],
		showTime = false
	) => showStarToast(workOrderId, roleType, badge, text, actions, showTime);
	const [isPersisting, setIsPersisting] = useState(false);

	const persist = (request: () => Promise<any>): Promise<any> => {
		setIsPersisting(true);
		return request()
			.then((result) => result || {})
			.catch(() => null)
			.finally(() => setIsPersisting(false));
	};

	const untrack = (previous: StarSnapshot): Promise<boolean> =>
		persist(() => removeWorkOrderStar(workOrderId)).then((result) => {
			if (result === null) return false;
			toast('untracked', 'Untracked', [
				{
					label: 'UNDO',
					onClick: () => {
						const undoRemindAt = restorableRemindAt(previous.remindAt);
						track(previous.note, undoRemindAt, null, previous.condition);
					},
				},
			]);
			return true;
		});

	const showToastForAction = (
		action: string,
		note: string | null,
		remindAt: moment.Moment | null,
		previous: StarSnapshot | null
	) => {
		switch (action) {
			case STAR_ACTION.STARRED:
				return remindAt ? toast('bell', 'Tracked and set reminder for') : toast('star', 'Tracked');
			case STAR_ACTION.REMINDER_SET:
				return toast('bell', 'Set reminder for');
			case STAR_ACTION.REMINDER_UPDATED:
				return toast('bell', 'Updated reminder for');
			case STAR_ACTION.REMINDER_SNOOZED:
				return toast('bell', 'Snoozed reminder for');
			case STAR_ACTION.REMINDER_STOPPED:
				return toast(
					'star',
					'Stopped reminder for',
					[
						// A stopped reminder was already due, and the backend rejects a past time, so
						// UNDO brings it back a minute from now to ring again.
						{
							label: 'UNDO',
							onClick: () =>
								track(
									note,
									moment().add(1, 'minute').millisecond(0),
									null,
									previous ? previous.condition : null
								),
						},
						{ label: 'UNTRACK', onClick: () => untrack({ note, remindAt: null }) },
					],
					true
				);
			case STAR_ACTION.REMINDER_REMOVED: {
				const canUndo = !!previous && !!restorableRemindAt(previous.remindAt);
				return toast(
					'star',
					'Removed reminder for',
					canUndo
						? [
								{
									label: 'UNDO',
									// Re-checked on press: the old time may have passed while the toast was up.
									// Then there is nothing to restore (the backend rejects a past reminder),
									// and saving without one would read as a successful undo.
									onClick: () => {
										const undoRemindAt = restorableRemindAt(previous.remindAt);
										if (!undoRemindAt) {
											message.warning('That reminder time has already passed');
											return;
										}
										track(note, undoRemindAt, null, previous.condition);
									},
								},
						  ]
						: []
				);
			}
			default:
				// note_updated, or nothing changed: the popover closing is feedback enough.
				return undefined;
		}
	};

	// `previous` is the star before this change (null when the WO was not tracked). `condition` is
	// the reminder's "Remind me only if…", dropped when there is no reminder.
	function track(
		note: string | null,
		remindAt: moment.Moment | null,
		previous: StarSnapshot | null,
		condition: string | null = null
	): Promise<boolean> {
		return persist(() => upsertWorkOrderStar(workOrderId, note, remindAt, condition)).then(
			(saved) => {
				if (saved === null) return false;
				showToastForAction(saved.action, note, remindAt, previous);
				return true;
			}
		);
	}

	// Snoozing moves the reminder and keeps its condition; stopping ends both.
	const snooze = (current: StarSnapshot, until: moment.Moment) =>
		track(current.note, until, current, current.condition);

	const stop = (current: StarSnapshot) => track(current.note, null, current);

	return { isPersisting, track, untrack, snooze, stop };
};

export default useStarActions;
